import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { Grid, WithStyles } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import {
  deleteSession,
  getSessionDetails,
  AssignExamTaskToExamTaskGroup,
} from '../../../services/exams/sessions/sessions.service';
import {
  IExamSession,
  IExamSessionDetails,
  Exam,
  IExamDetails,
  ExamSheet,
  IExamSheetDetails,
  Scheme,
  ISchemeDetails,
  Task,
  ITaskDetails,
  TaskGroup,
  ITaskGroupDetails,
  GradeRules,
  IGradeRulesDetails,
  sessionStatusType,
  ISessionItemDetails,
  ISessionItems,
  ICriteriaSetupDetails,
  CriteriaSetup,
  IIdResult,
  ExamSessionSystemType,
} from '../../../models/session.types';
import { ICommonDetailsProps } from '../DetailsProps.types';
import {
  TreeItemData,
  WorkMode,
  ICommonSessionTreeItemProps,
  NewTreeItemId,
} from '../../SessionTree/SessionTree.types';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import SessionTreeItem from '../../SessionTree/SessionTreeItem.component';
import SessionDetails from '../SessionDetails.component';
import ExamDetails from '../ExamDetails.component';
import ExamSheetDetails from '../ExamSheetDetails.component';
import TaskGroupDetails from '../TaskGroupDetails.component';
import TaskDetails from '../TaskDetails.component';
import GradeRulesDetails from '../GradeRulesDetails.component';
import SchemeScoresDetails from '../SchemeScoresDetails.component';
import { DialogBreadcrumbs } from '../../DialogBreadcrumbs/DialogBreadcrumbs.component';
import { Prompt } from '../../Prompt/Prompt.component';
import { BreadcrumbLevel } from '../../DialogBreadcrumbs/DialogBreadcrumbs.types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useStyles, styles } from './SessionDetailsModal.styles';
import { useLayoutContext } from '../../../Context/Layout.context';
import _ from 'lodash';
import { AxiosResponse } from 'axios';
import SessionTree from '../../SessionTree/SessionTree.component';
import { sessionTreeDragItemType } from '../../SessionTree/SessionTreeDragItemType';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  deleteExamScoringSchema,
  deleteExamSheet,
  deleteExamTask,
  deleteExamTaskGroup,
} from '../../../services/exams/examTaskGroup/examTaskGroup.service';
import { deleteScoringState, deleteSheetState, deleteState } from '../../../models/assessment.types';
import useSessionUser from '../../../hooks/useSessionUser';
import { ExaminerFunction } from '../../../pages/Assessment/Assessment.types';
import CriteriaSetupDetails from '../CriteriaSetupDetails.components';
import { NoDependentCriteria } from '../NoDependentCritria.component';
import CloseButton from '../../buttons/CloseButtton.component';
import { deleteExam } from '../../../services/exams/exam.service';

const ID_SEPARATOR = '-';

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  sessionId?: string;
  isOpen?: boolean;
  isOpenInEditMode?: boolean;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, ...other } = props;

  return (
    <MuiDialogTitle className={classes.root} {...other}>
      {children}
    </MuiDialogTitle>
  );
});

interface ISessionDetailsModalProps {
  sessionId: string | null;
  disableEditingSessionAndExam?: boolean;
  reloadOnClose: () => void;
  onClose?: () => void;
}

export default function SessionDetailsModal(props: ISessionDetailsModalProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [zoomedOut, setZoomedOut] = useState<boolean>(false);
  const [session, setSession] = useState<IExamSession>();
  const [isSessionNameExpanded, setIsSessionNameExpanded] = useState<boolean>(true);
  const [activeTreeLevel, setActiveTreeLevel] = useState<BreadcrumbLevel>(BreadcrumbLevel.SESSION);
  const [activeTreeId, setActiveTreeId] = useState<string>('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [editedItemId, setEditedItemId] = useState<string | null>('');
  const [activeSessionDetails, setActiveSessionDetails] = useState<IExamSessionDetails | undefined>();
  const [activeExamDetails, setActiveExamDetails] = useState<IExamDetails>();
  const [activeExamSheetDetails, setActiveExamSheetDetails] = useState<IExamSheetDetails>();
  const [activeTaskGroupDetails, setActiveTaskGroupDetails] = useState<ITaskGroupDetails>();
  const [activeTaskDetails, setActiveTaskDetails] = useState<ITaskDetails>();
  const [activeGradeRulesDetails, setActiveGradeRulesDetails] = useState<IGradeRulesDetails>();
  const [activeSchemeScoresDetails, setActiveSchemeScoresDetails] = useState<ISchemeDetails>();
  const [activeCriteriaSetupDetails, setActiveCriteriaSetupDetails] = useState<ICriteriaSetupDetails>();
  const [isTrainingSession, setIsTrainingSession] = useState<boolean>(false);

  const [workMode, setWorkMode] = useState<WorkMode>(WorkMode.VIEW);
  const [shouldReloadOnClose, setShouldReloadOnClose] = useState<boolean>(false);

  const shouldReloadAfterSave = true;

  const [deleteConfirmationPromptOpen, setDeleteConfirmationPromptOpen] = useState<boolean>(false);
  const [deleteErrorPromptOpen, setDeleteErrorPromptOpen] = useState<boolean>(false);
  const [deleteSuccessPromptOpen, setDeleteSuccessPromptOpen] = useState<boolean>(false);
  const [sessionItems, setSessionItems] = useState<ISessionItems | undefined>(undefined);

  const classes = useStyles();
  const { openAlert, showLoader, isTooltipOpen } = useLayoutContext();
  const { userAssessmentSessionInfo } = useSessionUser();

  const criteriaSetup = {
    leadingSchema: { id: '', value: 0 },
    dependentSchema: { id: '', value: 0 },
    fromExamScoreValueForLeadingSchema: { id: '', value: 0 },
    toExamScoreValueForLeadingSchema: { id: '', value: 0 },
    fromExamScoreValueForDependentSchema: { id: '', value: 0 },
    toExamScoreValueForDependentSchema: { id: '', value: 0 },
  };

  const isSessionImported = (): boolean => {
    return (
      activeSessionDetails?.sourceSystemType !== undefined &&
      activeSessionDetails?.sourceSystemType !== ExamSessionSystemType.ONE
    );
  };

  const getSessionItems = (session: IExamSession, itemIdExtended: string): ISessionItems => {
    const separatedItemId: string[] = itemIdExtended.split(ID_SEPARATOR);
    const itemId = separatedItemId[0];

    const result: ISessionItems = {
      schemeScores: null,
      gradeRules: null,
      criteriaSetup: null,
      task: null,
      taskGroup: null,
      examSheet: null,
      exam: null,
    };

    const examIndex = session.exams.findIndex((exam: Exam) => {
      if (exam.id === itemId) {
        result.exam = exam;

        return true;
      } else {
        const sheetIndex = exam.examSheets.findIndex((sheet: ExamSheet) => {
          if (sheet.id === itemId) {
            result.examSheet = sheet;

            return true;
          } else {
            const taskGroupIndex = sheet.taskGroups.findIndex((taskGroup: TaskGroup) => {
              if (taskGroup.id === itemId) {
                result.taskGroup = taskGroup;

                return true;
              } else {
                const taskIndex = taskGroup.tasks.findIndex((task: Task) => {
                  if (task.id === itemId) {
                    result.task = task;

                    return true;
                  } else {
                    if (task.gradeRules) {
                      if (task.gradeRules.id === itemId) {
                        result.gradeRules = task.gradeRules;

                        return true;
                      } else {
                        const schemeIndex = task.gradeRules.schemas?.findIndex((scheme: Scheme) => {
                          if (scheme.id === itemId) {
                            result.schemeScores = scheme;

                            return true;
                          }
                        });

                        if (schemeIndex >= 0) {
                          result.schemeScores = task.gradeRules.schemas[schemeIndex];
                          result.gradeRules = task.gradeRules;

                          return true;
                        }
                      }
                    }
                  }
                });

                if (taskIndex >= 0) {
                  result.task = taskGroup.tasks[taskIndex];

                  return true;
                }
              }
            });

            if (taskGroupIndex >= 0) {
              result.taskGroup = sheet.taskGroups[taskGroupIndex];

              return true;
            }
          }
        });

        if (sheetIndex >= 0) {
          result.examSheet = exam.examSheets[sheetIndex];

          return true;
        }
      }
    });

    if (examIndex >= 0) {
      result.exam = session.exams[examIndex];
    }

    return result;
  };

  const isInputDisabledOnView: () => boolean = () => {
    const sessionIsImported = isSessionImported();
    const isExamSheetConfigured = sessionItems?.examSheet?.isConfigured ? true : false;

    switch (activeTreeLevel) {
      case BreadcrumbLevel.SESSION:
        return !!props.disableEditingSessionAndExam;
      case BreadcrumbLevel.EXAMSHEET:
        return false;
      case BreadcrumbLevel.GRADERULES:
        return false;
      case BreadcrumbLevel.CRITERIA_SETUP:
        return false;
      case BreadcrumbLevel.TASKGROUP:
        return false;
      case BreadcrumbLevel.EXAM:
        return !!props.disableEditingSessionAndExam || sessionIsImported;
      case BreadcrumbLevel.TASK:
      case BreadcrumbLevel.SCHEMA:
        return sessionIsImported || isExamSheetConfigured;
      default:
        return sessionIsImported;
    }
  };

  const isExamCodeDisabled: () => boolean = () => {
    return sessionItems?.exam?.examSheets[0]?.isConfigured ? true : false;
  };

  const activateItemDetails = (itemId: string, level: BreadcrumbLevel, session: IExamSession) => {
    if (!session) return;

    setActiveTreeLevel(level);
    setActiveTreeId(itemId);

    const sessionItems = getSessionItems(session, itemId);

    setSessionItems(sessionItems);

    switch (level) {
      case BreadcrumbLevel.EXAM:
        if (sessionItems.exam) {
          setActiveExamDetails({
            ...sessionItems.exam,
            parentId: session.id,
          });
        }
        break;
      case BreadcrumbLevel.EXAMSHEET:
        if (sessionItems.examSheet && sessionItems.exam) {
          setActiveExamSheetDetails({
            ...sessionItems.examSheet,
            examId: sessionItems.exam.id,
            parentId: sessionItems.exam.id,
          });
        }
        break;
      case BreadcrumbLevel.TASKGROUP:
        if (sessionItems.taskGroup && sessionItems.examSheet) {
          setActiveTaskGroupDetails({
            ...sessionItems.taskGroup,
            parentId: sessionItems.examSheet.id,
            isExamSheetConfigured: sessionItems.examSheet.isConfigured,
          });
        }
        break;
      case BreadcrumbLevel.TASK:
        if (sessionItems.task && sessionItems.taskGroup && sessionItems.examSheet) {
          setActiveTaskDetails({
            ...sessionItems.task!,
            taskGroupId: sessionItems.taskGroup.id,
            examSheetId: sessionItems.examSheet.id,
            parentId: sessionItems.taskGroup.id,
            isExamSheetConfigured: sessionItems.examSheet.isConfigured,
          });
        }
        break;
      case BreadcrumbLevel.GRADERULES:
        if (sessionItems.gradeRules && sessionItems.task) {
          setActiveGradeRulesDetails({
            ...sessionItems.gradeRules,
            parentId: sessionItems.task.id,
            isExamSheetConfigured: sessionItems.examSheet!.isConfigured,
          });
        }
        break;
      case BreadcrumbLevel.CRITERIA_SETUP:
        if (sessionItems.criteriaSetup && sessionItems.task) {
          setActiveCriteriaSetupDetails({
            ...sessionItems.criteriaSetup,
            parentId: sessionItems.task.id,
            isExamSheetConfigured: sessionItems.examSheet!.isConfigured,
          });
        }
        break;
      case BreadcrumbLevel.SCHEMA:
        if (sessionItems.schemeScores && sessionItems.gradeRules) {
          setActiveSchemeScoresDetails({
            ...sessionItems.schemeScores,
            gradeRuleId: sessionItems.gradeRules.id,
            parentId: sessionItems.gradeRules.id,
            isExamSheetConfigured: sessionItems.examSheet!.isConfigured,
            taskNumber: sessionItems.task!.taskNumber,
          });
        }
        break;
      default:
        break;
    }
  };

  const activateParent = (itemId: string, level: BreadcrumbLevel) => {
    if (session) {
      activateItemDetails(itemId, level, session);
    }
  };

  const handleClose = () => {
    if (shouldReloadOnClose) {
      props.reloadOnClose();
    }

    setOpen(false);
    setEditedItemId('');
    setActiveTreeId('');
    setActiveTreeLevel(BreadcrumbLevel.SESSION);
    setSession(undefined);
    setWorkMode(WorkMode.VIEW);
    props.onClose && props.onClose();
  };

  const removeUndefinedTreeItem = (session: IExamSession): IExamSession => {
    const updatedSession = _.cloneDeep(session);

    updatedSession.exams.forEach((exam: Exam) => {
      if (exam.id === NewTreeItemId.Exam) {
        updatedSession.exams.pop();
      }

      updatedSession.exams.forEach((exam: Exam) => {
        exam.examSheets.forEach((examSheet: ExamSheet) => {
          if (examSheet.id === NewTreeItemId.ExamSheet) {
            exam.examSheets.pop();
          }

          examSheet.taskGroups.forEach((taskGroup: TaskGroup) => {
            if (taskGroup.id === NewTreeItemId.TaskGroup) {
              examSheet.taskGroups.pop();
            }

            taskGroup.tasks.forEach((task: Task) => {
              if (task.id === NewTreeItemId.Task) {
                taskGroup.tasks.pop();
              }

              if (task.gradeRules?.id === NewTreeItemId.GradeRules) {
                task.gradeRules = null;
              }

              if (task.criteriaSetup?.id === NewTreeItemId.CriteriaSetup) {
                task.criteriaSetup = null;
              }

              if (task.gradeRules?.schemas) {
                task.gradeRules.schemas.forEach((scheme: Scheme) => {
                  if (scheme.id === NewTreeItemId.SchemeScores) {
                    task.gradeRules?.schemas.pop();
                  }
                });
              }
            });
          });
        });
      });
    });

    return updatedSession;
  };

  const ReloadPropsForElementItemTree = (id: string, level: BreadcrumbLevel, data: IExamSession) => {
    activateItemDetails(id, level, data);
    setWorkMode(WorkMode.VIEW);
  };

  const trackAddingMode = (level: BreadcrumbLevel, data: IExamSession, parentId: string) => {
    const updatedSession = removeUndefinedTreeItem(data);

    setSession(updatedSession);
    activateItemDetails(parentId, level, updatedSession);
  };

  const breadcrumbChangeHandler = React.useCallback(
    (level: BreadcrumbLevel, id: string) => {
      if (session) {
        activateItemDetails(id, level, session);
      }
    },
    [session],
  );

  const cancelGlobal = (parentId: string, level: BreadcrumbLevel) => {
    setActiveTreeLevel(level);
    setActiveTreeId(parentId);
    setEditedItemId('');
    setWorkMode(WorkMode.VIEW);

    if (session) {
      trackAddingMode(level, session, parentId);
    }
  };

  const handleOpenNewSessionData = (sessionId: string | null) => {
    const expanded: string[] = [];

    getSessionDetails(sessionId).then(
      (result: IExamSession) => {
        setSession(result);
        setActiveTreeLevel(BreadcrumbLevel.SESSION);
        setEditedItemId('');
        setActiveSessionDetails({ ...result, parentId: '' });
        setIsTrainingSession(result.isTraining);

        result.exams.forEach((exam: Exam) => {
          expanded.push(exam.id);

          exam.examSheets.forEach((examSheet: ExamSheet) => {
            expanded.push(examSheet.id);

            examSheet.taskGroups.forEach((taskGroup: TaskGroup) => {
              expanded.push(taskGroup.id);
            });
          });
        });

        setExpandedItems(expanded);
      },
      () => {
        openAlert('get_error');
        props.onClose && props.onClose();
      },
    );

    setOpen(true);
  };

  useEffect(() => {
    if (props.sessionId || (props.sessionId === null && userAssessmentSessionInfo.function === ExaminerFunction.KK)) {
      handleOpenNewSessionData(props.sessionId);
    }
  }, [props.sessionId, userAssessmentSessionInfo.function]);

  const removeSession = () => {
    if (session) {
      deleteSession(session.id)
        .then(
          () => {
            setDeleteSuccessPromptOpen(true);
          },
          () => {
            setDeleteErrorPromptOpen(true);
          },
        )
        .finally(() => {
          setWorkMode(WorkMode.VIEW);
        });
    }
  };

  const cancelEdit = () => {
    setEditedItemId('');
    setWorkMode(WorkMode.VIEW);
  };

  const reloadSessionAfterSave = (itemCreated: boolean, id?: string) => {
    if (session) {
      getSessionDetails(userAssessmentSessionInfo.function !== ExaminerFunction.KK ? session.id : null).then(
        (result: IExamSession) => {
          setSession(result);
          setIsTrainingSession(result.isTraining);
          setActiveSessionDetails({
            ...result,
            parentId: '',
          });

          if (!itemCreated) {
            ReloadPropsForElementItemTree(id ?? activeTreeId, activeTreeLevel, result);
          }
        },
        () => openAlert('error'),
      );
    }
  };

  const reloadSessionAfterRemove = (id?: string | null) => {
    if (session) {
      getSessionDetails(userAssessmentSessionInfo.function !== ExaminerFunction.KK ? session.id : null).then(
        (result: IExamSession) => {
          setSession(result);
          setIsTrainingSession(result.isTraining);
        },
        () => openAlert('error'),
      );
    }
  };

  const onAddSessionTreeItem = (level: BreadcrumbLevel, parentId: string, examSheetId = '') => {
    if (!session) return;

    const updatedSession = removeUndefinedTreeItem(session);
    const sessionItems = getSessionItems(updatedSession, parentId);
    const expanded: string[] = [];
    const defaultNewItemAttributes = {
      parentId,
      examSheetId,
    };

    switch (level) {
      case BreadcrumbLevel.EXAM: {
        const exam: Exam = {
          id: NewTreeItemId.Exam,
          code: '',
          name: '',
          examSheets: [],
        };

        updatedSession.exams.push(exam);
        setActiveExamDetails({ ...exam, ...defaultNewItemAttributes });
        setActiveTreeId(NewTreeItemId.Exam);
        break;
      }
      case BreadcrumbLevel.EXAMSHEET: {
        const currentExam = sessionItems.exam;

        if (currentExam?.examSheets) {
          const examSheet: ExamSheet = {
            id: NewTreeItemId.ExamSheet,
            code: '',
            name: '',
            examId: currentExam.id,
            gradeRulesImageFilePath: '',
            sourceSystemId: '',
            taskGroups: [],
            isConfigured: false,
            retentionPeriod: null,
          };

          currentExam.examSheets.push(examSheet);
          setActiveExamSheetDetails({
            ...examSheet,
            ...defaultNewItemAttributes,
          });
          expanded.push(currentExam.id);
          setActiveTreeId(NewTreeItemId.ExamSheet);
        }
        break;
      }
      case BreadcrumbLevel.TASKGROUP: {
        const currentExamSheet = sessionItems.examSheet;

        if (currentExamSheet?.taskGroups) {
          const taskGroup: TaskGroup = {
            id: NewTreeItemId.TaskGroup,
            name: '',
            assessmentSession: {
              id: '',
              dateFrom: '',
              dateTo: '',
              sessionStatus: sessionStatusType.NEW,
            },
            tasks: [],
            examSheetId: currentExamSheet.id,
          };

          currentExamSheet.taskGroups.push(taskGroup);
          setActiveTaskGroupDetails({
            ...taskGroup,
            ...defaultNewItemAttributes,
            isExamSheetConfigured: currentExamSheet!.isConfigured,
          });

          if (sessionItems.exam) {
            expanded.push(sessionItems.exam.id, currentExamSheet.id);
          }

          setActiveTreeId(NewTreeItemId.TaskGroup);
        }
        break;
      }
      case BreadcrumbLevel.TASK: {
        const currentTaskGroup = sessionItems.taskGroup;
        const currentExamSheet = sessionItems.examSheet;

        if (currentTaskGroup?.tasks) {
          const task: Task = {
            id: NewTreeItemId.Task,
            taskNumber: '',
            gradeRules: null,
            criteriaSetup: null,
          };

          currentTaskGroup.tasks.push(task);
          setActiveTaskDetails({
            ...task,
            taskGroupId: parentId,
            ...defaultNewItemAttributes,
            isExamSheetConfigured: currentExamSheet!.isConfigured,
          });

          if (sessionItems.exam && sessionItems.examSheet) {
            expanded.push(sessionItems.exam.id, sessionItems.examSheet.id, currentTaskGroup.id);
          }

          setActiveTreeId(NewTreeItemId.Task);
        }
        break;
      }
      case BreadcrumbLevel.GRADERULES: {
        const currentTask = sessionItems.task;
        const currentExamSheet = sessionItems.examSheet;

        if (currentTask) {
          if (!currentTask.gradeRules) {
            const gradeRules: GradeRules = {
              id: NewTreeItemId.GradeRules,
              automaticScoring: false,
              noAnswer: false,
              content: '',
              schemas: [],
            };

            currentTask.gradeRules = gradeRules;
            setActiveGradeRulesDetails({
              ...gradeRules,
              ...defaultNewItemAttributes,
              isExamSheetConfigured: currentExamSheet!.isConfigured,
            });
          }

          if (sessionItems.exam && sessionItems.examSheet && sessionItems.taskGroup) {
            expanded.push(sessionItems.exam.id, sessionItems.examSheet.id, sessionItems.taskGroup.id, currentTask.id);
          }
        }

        setActiveTreeId(NewTreeItemId.GradeRules);
        break;
      }
      case BreadcrumbLevel.CRITERIA_SETUP: {
        const currentTask = sessionItems.task;
        const currentExamSheet = sessionItems.examSheet;

        if (currentTask) {
          if (!currentTask.criteriaSetup) {
            const criteriaSetup: CriteriaSetup = {
              id: NewTreeItemId.CriteriaSetup,
              criteriaSetupId: '',
              leadingSchema: { id: '', value: 0 },
              dependentSchema: { id: '', value: 0 },
              fromExamScoreValueForLeadingSchema: { id: '', value: 0 },
              toExamScoreValueForLeadingSchema: { id: '', value: 0 },
              fromExamScoreValueForDependentSchema: { id: '', value: 0 },
              toExamScoreValueForDependentSchema: { id: '', value: 0 },
            };

            currentTask.criteriaSetup = criteriaSetup;
            setActiveCriteriaSetupDetails({
              ...criteriaSetup,
              ...defaultNewItemAttributes,
              isExamSheetConfigured: currentExamSheet!.isConfigured,
            });
          }

          if (sessionItems.exam && sessionItems.examSheet && sessionItems.taskGroup) {
            expanded.push(sessionItems.exam.id, sessionItems.examSheet.id, sessionItems.taskGroup.id, currentTask.id);
          }
        }

        setActiveTreeId(NewTreeItemId.CriteriaSetup);
        break;
      }
      case BreadcrumbLevel.SCHEMA: {
        const currentGradeRules = sessionItems.gradeRules;
        const currentExamSheet = sessionItems.examSheet;

        if (currentGradeRules?.schemas) {
          const scheme: Scheme = {
            id: NewTreeItemId.SchemeScores,
            gradeRuleId: currentGradeRules.id,
            number: '',
            label: '',
            name: '',
            scores: [],
            isCommentRequiredEnabled: true,
            isUsedInCriteriaSetups: false,
          };

          currentGradeRules.schemas.push(scheme);
          setActiveSchemeScoresDetails({
            ...scheme,
            ...defaultNewItemAttributes,
            isExamSheetConfigured: currentExamSheet!.isConfigured,
            taskNumber: sessionItems.task!.taskNumber,
          });

          if (sessionItems.exam && sessionItems.examSheet && sessionItems.taskGroup && sessionItems.task) {
            expanded.push(
              sessionItems.exam.id,
              sessionItems.examSheet.id,
              sessionItems.taskGroup.id,
              sessionItems.task.id,
              currentGradeRules.id,
            );
          }

          setActiveTreeId(NewTreeItemId.SchemeScores);
        }
        break;
      }
      default: {
        throw new Error('Unknown level in adding new item');
      }
    }

    setActiveTreeLevel(level);
    setExpandedItems(expanded);
    setWorkMode(WorkMode.ADD);
    setSession(updatedSession);
  };

  const handleExamTaskToExamTaskGroupDrop = (targetId: string, draggedItemId: string): void => {
    AssignExamTaskToExamTaskGroup(targetId, draggedItemId)
      .then((result: AxiosResponse<IIdResult>) => {
        reloadSessionAfterSave(false, result.data.id);
      })
      .catch(() => {
        openAlert('error');
      });
  };

  const canDropExamTaskToExamTaskGroupDrop = (target: TreeItemData): boolean => {
    const taskGroup: TaskGroup = target as TaskGroup;

    return (
      !taskGroup.assessmentSession ||
      (taskGroup.assessmentSession &&
        taskGroup.assessmentSession.sessionStatus &&
        taskGroup.assessmentSession.sessionStatus === sessionStatusType.NEW)
    );
  };

  const onCloseSessionTreeItem = (data: ISessionItemDetails, level: BreadcrumbLevel, onRevert: () => void) => {
    if (workMode === WorkMode.ADD) {
      cancelGlobal(data.parentId, level);
    }

    cancelEdit();
    onRevert();
  };

  const onEditSessionTreeItem = (data: ISessionItemDetails, onCancelEdit?: () => void) => {
    if (workMode !== WorkMode.EDIT) {
      setEditedItemId(data.id);
      setWorkMode(WorkMode.EDIT);
    } else if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const commonSessionTreeItemProps: ICommonSessionTreeItemProps = {
    activeTreeId,
    activeTreeLevel,
    setActiveTreeId,
    setActiveTreeLevel,
    editedItemId,
    setEditedItemId,
    isSessionImported: isSessionImported(),
    workMode,
    setWorkMode,
    expandedItems,
    setExpandedItems,
  };

  const commonDetailsProps: ICommonDetailsProps = {
    isSessionImported: isSessionImported(),
    removeTreeItem: () => setDeleteConfirmationPromptOpen(true),
    setDeleteConfirmationPromptOpen,
    setActiveTreeLevel,
    isInputDisabled: isInputDisabledOnView,
    workMode,
    setWorkMode,
    onClose: onCloseSessionTreeItem,
    onEdit: onEditSessionTreeItem,
    onAddChild: onAddSessionTreeItem,
  };

  useEffect(() => {
    if (session) {
      const sessionItemsObj = getSessionItems(session, activeTreeId);

      setSessionItems(sessionItemsObj);
    }
  }, [
    activeTreeId,
    activeSessionDetails,
    activeExamDetails,
    activeExamSheetDetails,
    activeTaskGroupDetails,
    activeTaskDetails,
    activeGradeRulesDetails,
    activeSchemeScoresDetails,
    activeCriteriaSetupDetails,
  ]);

  //Delete Exam

  const [canExamBeDeleted, setCanExamBeDeleted] = useState<boolean>(false);

  const handleDeleteExamModals = () => {
    if (!(activeTaskGroupDetails && activeTaskGroupDetails.tasks.length > 0)) {
      setCanExamBeDeleted(true);
    }
  };

  const handleExamDelete = () => {
    showLoader(true);

    deleteExam(editedItemId)
      .then(
        () => {
          if (session) {
            openAlert('delete_success');
            setCanExamBeDeleted(false);
            setEditedItemId('');
            setActiveTreeLevel(BreadcrumbLevel.SESSION);
            setWorkMode(WorkMode.VIEW);
            reloadSessionAfterRemove(session.id);
          }
        },
        () => {
          openAlert('delete_error');
          setCanExamBeDeleted(false);
        },
      )
      .finally(() => showLoader(false));
  };

  //Delete Task Group

  const [canBeDeleted, setCanBeDeleted] = useState<boolean>(false);
  const [hasTaskAssigned, setHasTaskAssigned] = useState<boolean>(false);

  const handleDeleteModals = () => {
    if (activeTaskGroupDetails && activeTaskGroupDetails.tasks.length > 0) {
      setHasTaskAssigned(true);
    } else {
      setCanBeDeleted(true);
    }
  };

  const handleDelete = () => {
    deleteExamTaskGroup(userAssessmentSessionInfo.function !== ExaminerFunction.KK ? editedItemId : null).then(
      (result) => {
        if (result === deleteState.EXAMTASKEXIST) {
          setHasTaskAssigned(true);
        }

        if (result === deleteState.SUCCESS && session) {
          openAlert('delete_success');
          setCanBeDeleted(false);
          setHasTaskAssigned(false);
          setEditedItemId('');
          setActiveTreeLevel(BreadcrumbLevel.EXAMSHEET);
          setWorkMode(WorkMode.VIEW);
          reloadSessionAfterRemove(userAssessmentSessionInfo.function !== ExaminerFunction.KK ? session.id : null);
        }
      },
      () => {
        openAlert('delete_error');
        setCanBeDeleted(false);
        setHasTaskAssigned(false);
      },
    );
  };

  //ExamSheet deleting
  const [canSheetBeDeleted, setCanSheetBeDeleted] = useState<boolean>(false);

  const handleSheetDeleteModal = () => {
    setCanSheetBeDeleted(true);
  };

  const handleSheetDelete = () => {
    deleteExamSheet(editedItemId).then(
      (result) => {
        if (result === deleteSheetState.SUCCESS && session) {
          openAlert('delete_success');
          setCanSheetBeDeleted(false);
          setEditedItemId('');
          setActiveTreeLevel(BreadcrumbLevel.EXAM);
          setWorkMode(WorkMode.VIEW);
          reloadSessionAfterRemove(session.id);
        }
      },
      () => {
        openAlert('delete_error');
        setCanSheetBeDeleted(false);
      },
    );
  };

  //Task deleting
  const [canTaskBeDeleted, setCanTaskBeDeleted] = useState<boolean>(false);

  const handleTaskDeleteModal = () => {
    setCanTaskBeDeleted(true);
  };

  const handleTaskDelete = () => {
    deleteExamTask(editedItemId).then(
      (result) => {
        if (result === deleteSheetState.SUCCESS && session) {
          openAlert('delete_success');
          setCanTaskBeDeleted(false);
          setEditedItemId('');
          setActiveTreeLevel(BreadcrumbLevel.TASKGROUP);
          setWorkMode(WorkMode.VIEW);
          reloadSessionAfterRemove(session.id);
        }
      },
      () => {
        openAlert('delete_error');
        setCanTaskBeDeleted(false);
      },
    );
  };

  const IsSioePkz = session?.sourceSystemName === 'SIOEPKZ';

  //ShemeScores deleting
  const [canSchemeScoresBeDeleted, setCanSchemeScoresBeDeleted] = useState<boolean>(false);

  const handleSchemeScoresDeleteModal = useCallback(() => {
    setCanSchemeScoresBeDeleted(true);
  }, []);

  const handleSchemeScoresDelete = () => {
    deleteExamScoringSchema(editedItemId).then(
      (result) => {
        if (result === deleteScoringState.SUCCESS && session) {
          openAlert('delete_success');
          setCanSchemeScoresBeDeleted(false);
          setActiveTreeLevel(BreadcrumbLevel.GRADERULES);
          setEditedItemId('');
          setWorkMode(WorkMode.VIEW);
          reloadSessionAfterRemove(session.id);
        }
      },
      () => {
        openAlert('delete_error');
        setCanSchemeScoresBeDeleted(false);
      },
    );
  };

  const handleResize = () => {
    if (window.devicePixelRatio < 1) {
      setZoomedOut(true);
    } else {
      setZoomedOut(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      //clean up
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div>
      {session && open && (
        <Dialog
          onClose={(event, reason) => {
            if (reason !== 'backdropClick') {
              handleClose();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !isTooltipOpen.current) {
              handleClose();
            }
          }}
          aria-label="Szczegóły sesji egzaminacyjnej"
          open={open}
          maxWidth={false}
        >
          <DialogTitle id="customized-dialog-title" onClose={handleClose}>
            <Grid container className={zoomedOut ? classes.flexContent : ''}>
              <Grid item xs={6}>
                <div className={classes.modalTitle}>
                  <TooltipOne title={`Szczegóły sesji egzaminacyjnej: ${session.name}`}>
                    <p className={classes.maxTextItem}>
                      Szczegóły sesji egzaminacyjnej {isTrainingSession ? '(treningowej)' : ''}: {session.name}
                    </p>
                  </TooltipOne>
                </div>
              </Grid>
              <Grid item xs={6}>
                <CloseButton onClick={handleClose} />
              </Grid>
              <Prompt
                type="confirmation"
                open={deleteConfirmationPromptOpen}
                mainText="Czy na pewno chcesz usunąć sesję egzaminacyjną?"
                subTexts={[
                  {
                    text: 'Usunięcie sesji egzaminacyjnej spowoduje usunięcie wszystkich wcześniej zdefiniowanych parametrów.',
                  },
                ]}
                mainActionButtonText="TAK"
                onConfirm={() => {
                  setDeleteConfirmationPromptOpen(false);
                  removeSession();
                }}
                onCancel={() => {
                  setDeleteConfirmationPromptOpen(false);
                }}
              />
              <Prompt
                type="error"
                open={deleteErrorPromptOpen}
                mainText="Usunięcie danych sesji egzaminacyjnej nie powiodło się."
                subTexts={[{ text: 'Spróbuj ponownie później.' }]}
                mainActionButtonText="POWRÓT DO EDYCJI"
                onConfirm={() => {
                  setDeleteErrorPromptOpen(false);
                }}
              />
              <Prompt
                type="success"
                open={deleteSuccessPromptOpen}
                mainText="Usunięcie danych sesji egzaminacyjnej zakończone sukcesem."
                mainActionButtonText="POWRÓT DO LISTY"
                onConfirm={() => {
                  setDeleteSuccessPromptOpen(false);
                  setOpen(false);

                  props.reloadOnClose();
                }}
              />
              <div className={classes.modalBody}>
                <Grid container className={classes.gridBorder}>
                  <Grid item xs={4}>
                    <div className={classes.leftMenu} id="scroll-bar">
                      <div className={classes.sessionTree}>
                        <DndProvider backend={HTML5Backend}>
                          <SessionTree
                            data={session}
                            isSessionNameExpanded={isSessionNameExpanded}
                            setIsSessionNameExpanded={setIsSessionNameExpanded}
                            level={BreadcrumbLevel.SESSION}
                            translateName={'translate(35px, 0)'}
                            translateIcon={'15px'}
                            removeTreeItem={() => setDeleteConfirmationPromptOpen(true)}
                            {...commonSessionTreeItemProps}
                            activeTreeId={session.id}
                            isReadOnly={isInputDisabledOnView}
                            isTrainingSession={isTrainingSession}
                          />
                          {isSessionNameExpanded &&
                            session.exams.map((exam) => (
                              <React.Fragment key={exam.id}>
                                <SessionTreeItem
                                  data={exam}
                                  level={BreadcrumbLevel.EXAM}
                                  onActivate={() => {
                                    setActiveExamDetails({
                                      ...exam,
                                      parentId: session.id,
                                    });
                                  }}
                                  indent={'5px'}
                                  {...commonSessionTreeItemProps}
                                  isReadOnly={isInputDisabledOnView}
                                  isExamCodeReadOnly={isExamCodeDisabled}
                                  hideDeleteIcon={activeExamDetails && activeExamDetails.examSheets.length > 0}
                                  removeTreeItem={handleDeleteExamModals}
                                />
                                {expandedItems.indexOf(exam.id) !== -1 &&
                                  exam.examSheets.map((examSheet) => (
                                    <React.Fragment key={examSheet.id}>
                                      <SessionTreeItem
                                        data={examSheet}
                                        level={BreadcrumbLevel.EXAMSHEET}
                                        onActivate={() => {
                                          setActiveExamSheetDetails({
                                            ...examSheet,
                                            examId: exam.id,
                                            parentId: exam.id,
                                          });
                                        }}
                                        indent={'35px'}
                                        {...commonSessionTreeItemProps}
                                        isReadOnly={isInputDisabledOnView}
                                        isSheetConfigured={examSheet.isConfigured === true}
                                        itemTitleIcon={
                                          examSheet.isConfigured ? (
                                            <TooltipOne title="Konfiguracja arkusza została zakończona">
                                              <LockOutlinedIcon className={classes.lockIcon} />
                                            </TooltipOne>
                                          ) : undefined
                                        }
                                        removeTreeItem={handleSheetDeleteModal}
                                      />
                                      {expandedItems.indexOf(examSheet.id) !== -1 &&
                                        examSheet.taskGroups.map((taskGroup) => (
                                          <React.Fragment key={taskGroup.id}>
                                            <SessionTreeItem
                                              data={taskGroup}
                                              level={BreadcrumbLevel.TASKGROUP}
                                              dropType={sessionTreeDragItemType.TASK}
                                              dropDisabled={examSheet.isConfigured}
                                              onDropTreeItem={handleExamTaskToExamTaskGroupDrop}
                                              canDropTreeItem={canDropExamTaskToExamTaskGroupDrop}
                                              removeTreeItem={handleDeleteModals}
                                              isSheetConfigured={examSheet.isConfigured === true}
                                              onActivate={() => {
                                                setActiveTaskGroupDetails({
                                                  ...taskGroup,
                                                  parentId: examSheet.id,
                                                  isExamSheetConfigured: examSheet.isConfigured,
                                                });
                                              }}
                                              indent={'45px'}
                                              {...commonSessionTreeItemProps}
                                              isReadOnly={isInputDisabledOnView}
                                            >
                                              {expandedItems.indexOf(taskGroup.id) !== -1 &&
                                                taskGroup.tasks.map((task) => (
                                                  <React.Fragment key={task.id}>
                                                    <SessionTreeItem
                                                      data={task}
                                                      level={BreadcrumbLevel.TASK}
                                                      dragType={sessionTreeDragItemType.TASK}
                                                      dragDisabled={examSheet.isConfigured}
                                                      onActivate={() => {
                                                        setActiveTaskDetails({
                                                          ...task,
                                                          taskGroupId: taskGroup.id,
                                                          examSheetId: examSheet.id,
                                                          parentId: taskGroup.id,
                                                          isExamSheetConfigured: examSheet.isConfigured,
                                                        });
                                                      }}
                                                      dragData={{
                                                        taskGroupStatus: taskGroup.assessmentSession.sessionStatus,
                                                        taskGroupId: taskGroup.id,
                                                        examSheetId: examSheet.id,
                                                      }}
                                                      indent={'55px'}
                                                      {...commonSessionTreeItemProps}
                                                      taskGradeRulesId={task.gradeRules?.id}
                                                      isReadOnly={isInputDisabledOnView}
                                                      removeTreeItem={handleTaskDeleteModal}
                                                    />
                                                    {expandedItems.indexOf(task.id) !== -1 &&
                                                      task.gradeRules &&
                                                      !!task.gradeRules.schemas && (
                                                        <SessionTreeItem
                                                          data={task.gradeRules}
                                                          level={BreadcrumbLevel.GRADERULES}
                                                          onActivate={() => {
                                                            setActiveGradeRulesDetails({
                                                              ...task.gradeRules!,
                                                              parentId: task.id,
                                                              isExamSheetConfigured: examSheet.isConfigured,
                                                            });
                                                          }}
                                                          indent={'65px'}
                                                          {...commonSessionTreeItemProps}
                                                          isReadOnly={isInputDisabledOnView}
                                                        />
                                                      )}
                                                    {task.gradeRules &&
                                                      expandedItems.indexOf(task.gradeRules.id) !== -1 &&
                                                      task.gradeRules?.schemas.map((scheme) => (
                                                        <React.Fragment key={scheme.id}>
                                                          <SessionTreeItem
                                                            data={scheme}
                                                            level={BreadcrumbLevel.SCHEMA}
                                                            onActivate={() => {
                                                              setActiveSchemeScoresDetails({
                                                                ...scheme,
                                                                gradeRuleId: task.gradeRules!.id,
                                                                parentId: task.gradeRules!.id,
                                                                isExamSheetConfigured: examSheet.isConfigured,
                                                                taskNumber: task.taskNumber,
                                                              });
                                                            }}
                                                            indent={'75px'}
                                                            {...commonSessionTreeItemProps}
                                                            isReadOnly={isInputDisabledOnView}
                                                            removeTreeItem={handleSchemeScoresDeleteModal}
                                                          />
                                                        </React.Fragment>
                                                      ))}
                                                    {expandedItems.indexOf(task.id) !== -1 &&
                                                      !IsSioePkz &&
                                                      task.gradeRules &&
                                                      task.gradeRules.id &&
                                                      !!task.gradeRules.schemas && (
                                                        <SessionTreeItem
                                                          data={{
                                                            id: `${task.id}${ID_SEPARATOR}criteriaSetup`,
                                                            ...criteriaSetup,
                                                          }}
                                                          level={BreadcrumbLevel.CRITERIA_SETUP}
                                                          onActivate={() => {
                                                            setActiveCriteriaSetupDetails({
                                                              id: task.id,
                                                              ...criteriaSetup,
                                                              gradeRuleId: task.gradeRules?.id,
                                                              schemas: task.gradeRules?.schemas,
                                                              parentId: task.id,
                                                              isExamSheetConfigured: examSheet.isConfigured,
                                                            });
                                                          }}
                                                          indent={'65px'}
                                                          {...commonSessionTreeItemProps}
                                                          isReadOnly={isInputDisabledOnView}
                                                        />
                                                      )}
                                                  </React.Fragment>
                                                ))}
                                            </SessionTreeItem>
                                          </React.Fragment>
                                        ))}
                                    </React.Fragment>
                                  ))}
                              </React.Fragment>
                            ))}
                        </DndProvider>
                      </div>
                    </div>
                  </Grid>
                  <Grid xs={8} style={{}}>
                    <>
                      <div className={classes.detailsHeader}>
                        <div className={classes.breadcrumbs}>
                          <span
                            className={activeTreeLevel === BreadcrumbLevel.SESSION ? classes.detailsHeaderActive : ''}
                          >
                            {sessionItems && (
                              <DialogBreadcrumbs
                                id={activeTreeId}
                                level={activeTreeLevel}
                                session={session}
                                changeHandler={breadcrumbChangeHandler}
                                sessionItems={sessionItems}
                              />
                            )}
                          </span>
                        </div>
                      </div>
                    </>

                    {activeTreeLevel === BreadcrumbLevel.SESSION && activeSessionDetails && (
                      <SessionDetails
                        data={activeSessionDetails}
                        key={activeSessionDetails.name}
                        isReadOnly={isInputDisabledOnView}
                        onSave={(result: IIdResult) => {
                          if (shouldReloadAfterSave) {
                            reloadSessionAfterSave(false, result.id);
                          }

                          setShouldReloadOnClose(true);
                          setEditedItemId('');
                        }}
                        onDelete={() => setDeleteConfirmationPromptOpen(true)}
                        {...commonDetailsProps}
                        isSessionImported={isSessionImported()}
                      />
                    )}

                    {activeTreeLevel === BreadcrumbLevel.EXAM && activeExamDetails && (
                      <ExamDetails
                        data={activeExamDetails}
                        key={activeExamDetails.id}
                        isReadOnly={isInputDisabledOnView}
                        isExamCodeReadOnly={isExamCodeDisabled}
                        onSave={(result: IIdResult, itemCreated: boolean) => {
                          if (shouldReloadAfterSave) {
                            reloadSessionAfterSave(itemCreated, result.id);
                          }

                          setShouldReloadOnClose(true);
                          setEditedItemId('');

                          if (itemCreated) {
                            activateParent(activeExamDetails.parentId, BreadcrumbLevel.SESSION);
                            setActiveExamDetails(undefined);
                          }
                        }}
                        onDelete={handleDeleteExamModals}
                        sessionId={session.id}
                        {...commonDetailsProps}
                      />
                    )}

                    {activeTreeLevel === BreadcrumbLevel.EXAMSHEET && activeExamSheetDetails && (
                      <ExamSheetDetails
                        data={activeExamSheetDetails}
                        key={activeExamSheetDetails.id}
                        isReadOnly={isInputDisabledOnView}
                        onSave={(result: IIdResult, itemCreated: boolean) => {
                          if (shouldReloadAfterSave) {
                            reloadSessionAfterSave(itemCreated, result.id);
                          }

                          setShouldReloadOnClose(true);
                          setEditedItemId('');

                          if (itemCreated) {
                            activateParent(activeExamSheetDetails.parentId, BreadcrumbLevel.EXAM);
                            setActiveExamSheetDetails(undefined);
                          }
                        }}
                        onDelete={handleSheetDeleteModal}
                        {...commonDetailsProps}
                      />
                    )}

                    {activeTreeLevel === BreadcrumbLevel.TASKGROUP && activeTaskGroupDetails && (
                      <TaskGroupDetails
                        data={activeTaskGroupDetails}
                        key={activeTaskGroupDetails.id}
                        isReadOnly={isInputDisabledOnView}
                        onSave={(result: IIdResult, itemCreated: boolean) => {
                          if (shouldReloadAfterSave) {
                            reloadSessionAfterSave(itemCreated, result.id);
                          }

                          setShouldReloadOnClose(true);
                          setEditedItemId('');

                          if (itemCreated) {
                            activateParent(activeTaskGroupDetails.parentId, BreadcrumbLevel.EXAMSHEET);
                            setActiveTaskGroupDetails(undefined);
                          }
                        }}
                        onDelete={handleDeleteModals}
                        {...commonDetailsProps}
                      />
                    )}

                    {activeTreeLevel === BreadcrumbLevel.TASK && activeTaskDetails && (
                      <TaskDetails
                        data={activeTaskDetails}
                        key={activeTaskDetails.id}
                        isReadOnly={isInputDisabledOnView}
                        onSave={(result: IIdResult, itemCreated: boolean) => {
                          if (shouldReloadAfterSave) {
                            reloadSessionAfterSave(itemCreated, result.id);
                          }

                          setShouldReloadOnClose(true);
                          setEditedItemId('');

                          if (itemCreated) {
                            activateParent(activeTaskDetails.parentId, BreadcrumbLevel.TASKGROUP);
                            setActiveTaskDetails(undefined);
                          }
                        }}
                        {...commonDetailsProps}
                        onDelete={handleTaskDeleteModal}
                      />
                    )}

                    {activeTreeLevel === BreadcrumbLevel.GRADERULES && activeGradeRulesDetails && (
                      <GradeRulesDetails
                        data={activeGradeRulesDetails}
                        key={activeGradeRulesDetails.id}
                        isReadOnly={isInputDisabledOnView}
                        task={sessionItems?.task ?? null}
                        onSave={(result: IIdResult, itemCreated: boolean) => {
                          if (shouldReloadAfterSave) {
                            reloadSessionAfterSave(itemCreated, result.id);
                          }

                          setShouldReloadOnClose(true);
                          setEditedItemId('');

                          if (itemCreated) {
                            activateParent(activeGradeRulesDetails.parentId, BreadcrumbLevel.TASK);
                            setActiveGradeRulesDetails(undefined);
                          }
                        }}
                        {...commonDetailsProps}
                      />
                    )}

                    {activeTreeLevel === BreadcrumbLevel.CRITERIA_SETUP &&
                      activeCriteriaSetupDetails &&
                      (activeCriteriaSetupDetails.schemas && activeCriteriaSetupDetails.schemas.length > 1 ? (
                        <CriteriaSetupDetails
                          data={activeCriteriaSetupDetails}
                          key={activeCriteriaSetupDetails.id}
                          isReadOnly={isInputDisabledOnView}
                          onSave={(result: IIdResult, itemCreated: boolean) => {
                            if (shouldReloadAfterSave) {
                              reloadSessionAfterSave(itemCreated, result.id);
                            }

                            setShouldReloadOnClose(true);
                            setEditedItemId('');

                            if (itemCreated) {
                              activateParent(activeCriteriaSetupDetails.parentId, BreadcrumbLevel.GRADERULES);
                              setActiveCriteriaSetupDetails(undefined);
                            }
                          }}
                          {...commonDetailsProps}
                        />
                      ) : (
                        <NoDependentCriteria />
                      ))}

                    {activeTreeLevel === BreadcrumbLevel.SCHEMA && activeSchemeScoresDetails && (
                      <SchemeScoresDetails
                        data={activeSchemeScoresDetails}
                        key={activeSchemeScoresDetails.id}
                        activeTreeLevel={activeTreeLevel}
                        isReadOnly={isInputDisabledOnView}
                        onSave={(result: IIdResult, itemCreated: boolean) => {
                          if (shouldReloadAfterSave) {
                            reloadSessionAfterSave(itemCreated, result.id);
                          }

                          setShouldReloadOnClose(true);
                          setEditedItemId('');

                          if (itemCreated) {
                            activateParent(activeSchemeScoresDetails.parentId, BreadcrumbLevel.GRADERULES); //CRITERIA_SETUP);
                            setActiveSchemeScoresDetails(undefined);
                          }
                        }}
                        onDelete={handleSchemeScoresDeleteModal}
                        {...commonDetailsProps}
                        isSioePkz={IsSioePkz}
                      />
                    )}
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </DialogTitle>
        </Dialog>
      )}
      {(canBeDeleted || hasTaskAssigned) && (
        <Prompt
          type={canBeDeleted ? 'confirmation' : 'error'}
          open={true}
          size="sm"
          mainActionButtonText={canBeDeleted ? 'POTWIERDŹ' : 'POWRÓT'}
          onCancel={canBeDeleted ? () => setCanBeDeleted(false) : undefined}
          onConfirm={canBeDeleted ? () => handleDelete() : () => setHasTaskAssigned(false)}
          mainText={
            canBeDeleted
              ? `Czy na pewno chcesz usunąć`
              : `Nie można usunąć grupy zadań, ponieważ jest do niej przypisane przynajmniej 1 zadanie.`
          }
          subTexts={
            canBeDeleted && activeTaskGroupDetails
              ? [{ text: `Grupę zadań ${activeTaskGroupDetails.name}` }]
              : undefined
          }
        />
      )}
      {canExamBeDeleted && activeExamDetails && (
        <Prompt
          type={'confirmation'}
          open={true}
          size="sm"
          mainActionButtonText={'TAK'}
          onCancel={() => setCanExamBeDeleted(false)}
          onConfirm={() => handleExamDelete()}
          mainText={`Czy na pewno chcesz usunąć egzamin?`}
          subTexts={[
            {
              text: 'Usunięcie egzaminu jest operacją nieodwracalną. W przypadku konieczności przywrócenia egzaminu, należy dodać go ponownie.',
            },
          ]}
        />
      )}

      {canSheetBeDeleted && activeExamSheetDetails && (
        <Prompt
          type={'confirmation'}
          open={true}
          size="sm"
          mainActionButtonText={'TAK'}
          onCancel={() => setCanSheetBeDeleted(false)}
          onConfirm={() => handleSheetDelete()}
          mainText={`Czy na pewno chcesz usunąć Arkusz ${activeExamSheetDetails.name}`}
          subTexts={[
            {
              text: 'Usuwając arkusz usuwasz wszystkie powiązane z nim grupy zadań wraz z zasadami i schematem punktowania',
            },
          ]}
        />
      )}
      {canSchemeScoresBeDeleted && activeSchemeScoresDetails && (
        <Prompt
          type={'confirmation'}
          open={true}
          size="sm"
          mainActionButtonText={'TAK'}
          onCancel={() => setCanSchemeScoresBeDeleted(false)}
          onConfirm={() => handleSchemeScoresDelete()}
          mainText={`Czy na pewno chcesz usunąć schemat punktowania ${activeSchemeScoresDetails.name} dla zadania ${activeSchemeScoresDetails.taskNumber}`}
        />
      )}
      {canTaskBeDeleted && activeTaskDetails && (
        <Prompt
          type={'confirmation'}
          open={true}
          size="sm"
          mainActionButtonText={'TAK'}
          onCancel={() => setCanTaskBeDeleted(false)}
          onConfirm={handleTaskDelete}
          mainText={`Czy na pewno chcesz usunąć zadanie ${activeTaskDetails.taskNumber} ?`}
          subTexts={[
            {
              text: (
                <>
                  Usuwając zadanie usuwasz wszystkie powiązanie z nim zasady oceniania <br />i schematy punktowania
                </>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
